export type TaskHealth = 'healthy' | 'stale' | 'orphaned';

export interface TaskHealthInfo {
  health: TaskHealth;
  minutesSinceHeartbeat?: number;
  message?: string;
}

/**
 * Determines the health status of a task based on its current state and last heartbeat
 * @param task - The task object containing status and lastHeartbeat information
 * @returns TaskHealthInfo object with health status and additional information
 */
export function getTaskHealth(task: TradeDataLogsTypes): TaskHealthInfo {
  // For non-processing tasks or tasks without heartbeat data, consider them healthy
  if ((task.status !== 'processing' && task.status !== 'inprocess') || !task.lastHeartbeat) {
    return { health: 'healthy' };
  }

  const now = Date.now();
  const lastHeartbeatTime = new Date(task.lastHeartbeat).getTime();
  const minutesSinceHeartbeat = (now - lastHeartbeatTime) / 60000;

  if (minutesSinceHeartbeat > 10) {
    return {
      health: 'orphaned',
      minutesSinceHeartbeat,
      message: 'Task may be stuck'
    };
  }

  if (minutesSinceHeartbeat > 4) {
    return {
      health: 'stale',
      minutesSinceHeartbeat,
      message: 'Processing slow...'
    };
  }

  return {
    health: 'healthy',
    minutesSinceHeartbeat
  };
}

/**
 * Determines the appropriate polling interval based on task health
 * @param tasks - Array of tasks to analyze
 * @returns Polling interval in milliseconds
 */
export function getPollingInterval(tasks: TradeDataLogsTypes[]): number {
  const hasProcessingTasks = tasks.some(task => 
    task.status === 'processing' || task.status === 'inprocess'
  );

  return hasProcessingTasks ? 15000 : 60000; // 15s vs 60s
}